import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import type { ClientOperatingContextSummary } from "../types/operatingContext";
import type { MeResponse, OrgTokenSet } from "../types/orgAuth";

interface OrgAuthState {
    tokens: OrgTokenSet | null;
    me: MeResponse | null;
    availableClientContexts: ClientOperatingContextSummary[];
    defaultClientContextUuid: string | null;
    selectedClientContextUuid: string | null;
}

interface OrgAuthActions {
    setTokens: (tokens: OrgTokenSet) => void;
    setMe: (me: MeResponse) => void;
    setClientOperatingContexts: (
        availableClientContexts: ClientOperatingContextSummary[],
        defaultClientContextUuid: string | null
    ) => void;
    setSelectedClientContextUuid: (issuerClientUuid: string | null) => void;
    clearAuth: () => void;
}

function resolveSelectedClientContextUuid(
    currentSelection: string | null,
    availableClientContexts: ClientOperatingContextSummary[],
    defaultClientContextUuid: string | null
): string | null {
    const availableUuids = new Set(availableClientContexts.map((context) => context.issuerClientUuid));

    if (currentSelection) {
        return availableUuids.has(currentSelection) ? currentSelection : null;
    }

    if (defaultClientContextUuid && availableUuids.has(defaultClientContextUuid)) {
        return defaultClientContextUuid;
    }

    return null;
}

/**
 * In-session auth store for org console apps (issuer + client).
 *
 * Persisted to sessionStorage: survives page refresh, cleared on tab close.
 * Tokens are never written to localStorage.
 */
export const useOrgAuthStore = create<OrgAuthState & OrgAuthActions>()(
    persist(
        (set) => ({
            tokens: null,
            me: null,
            availableClientContexts: [],
            defaultClientContextUuid: null,
            selectedClientContextUuid: null,
            setTokens: (tokens): void => set({ tokens }),
            setMe: (me): void =>
                set((state) => ({
                    me,
                    availableClientContexts: me.availableClientContexts,
                    defaultClientContextUuid: me.defaultClientContextUuid,
                    selectedClientContextUuid: resolveSelectedClientContextUuid(
                        state.selectedClientContextUuid,
                        me.availableClientContexts,
                        me.defaultClientContextUuid
                    )
                })),
            setClientOperatingContexts: (availableClientContexts, defaultClientContextUuid): void =>
                set((state) => ({
                    availableClientContexts,
                    defaultClientContextUuid,
                    selectedClientContextUuid: resolveSelectedClientContextUuid(
                        state.selectedClientContextUuid,
                        availableClientContexts,
                        defaultClientContextUuid
                    )
                })),
            setSelectedClientContextUuid: (issuerClientUuid): void =>
                set((state) => ({
                    selectedClientContextUuid:
                        issuerClientUuid &&
                        state.availableClientContexts.some((context) => context.issuerClientUuid === issuerClientUuid)
                            ? issuerClientUuid
                            : null
                })),
            clearAuth: (): void =>
                set({
                    tokens: null,
                    me: null,
                    availableClientContexts: [],
                    defaultClientContextUuid: null,
                    selectedClientContextUuid: null
                })
        }),
        {
            name: "yaas-org-auth",
            storage: createJSONStorage(() => sessionStorage)
        }
    )
);
